/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { Injectable, computed, effect, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import type { Tables } from "@app/@classes/supabase";
import type { Session } from "@supabase/supabase-js";
import { Subject } from "rxjs";
import { LoggerService } from "./logger.service";

@Injectable({
	providedIn: "root",
})
export class AuthStateService {
	private readonly loggerService = inject(LoggerService);
	private readonly router = inject(Router);
	public readonly currentUserId = signal<string | undefined>(undefined);
	public readonly currentAuthSession = signal<Session | null>(null);
	public readonly currentUserInfo = signal<Tables<"users"> | null>(null);
	public readonly currentAppRole = signal<string | null>(null);

	public readonly currentTenantId = signal<string | undefined>(undefined);
	public readonly currentTenantInfo = signal<Tables<"tenants"> | null>(null);
	public readonly currentTenantRole = signal<string | null>(null);

	public readonly currentTenantId$ = new Subject<string | undefined>();
	public readonly currentUserId$ = new Subject<string | undefined>();
	public readonly currentAuthSession$ = new Subject<Session | null>();

	public readonly isSuperadmin = computed(
		() => this.currentAppRole() === "superadmin",
	);

	public readonly isTenantOwner = computed(
		() => this.currentTenantRole() === "owner" || this.isSuperadmin(),
	);
	public readonly isTenantAdmin = computed(
		() =>
			this.currentTenantRole() === "admin" ||
			this.isTenantOwner() ||
			this.isSuperadmin(),
	);

	constructor() {
		// Get the userId and tenantId from the session storage
		const userId = localStorage.getItem("userId");
		const tenantId = sessionStorage.getItem("tenantId");

		// Set the signals
		this.currentUserId.set(userId ?? undefined);
		this.currentTenantId.set(tenantId ?? undefined);

		effect(() => {
			this.currentUserId$.next(this.currentUserId());
		});

		effect(() => {
			this.currentTenantId$.next(this.currentTenantId());
		});

		effect(() => {
			this.currentAuthSession$.next(this.currentAuthSession());
		});

		effect(() => {
			// Everytime the userId or the tenantId changes, save it to the session storage
			const userId = this.currentUserId();
			const tenantId = this.currentTenantId();
			if (userId && tenantId) {
				localStorage.setItem("userId", userId);
				sessionStorage.setItem("tenantId", tenantId);
			}
		});

		effect(() => {
			const data = {
				tenantId: this.currentTenantId(),
				userId: this.currentUserId(),
				userInfo: this.currentUserInfo(),
				appRole: this.currentAppRole(),
				tenantInfo: this.currentTenantInfo(),
				tenantRole: this.currentTenantRole(),
				session: this.currentAuthSession(),
			};

			this.loggerService.logInfo("[AuthStateService] Current data", data);
		});

		// effect(() => {
		// 	const currentTenantRole = this.currentTenantRole();
		// 	if (currentTenantRole === "warehouseman") {
		// 		this.router.navigate([CbRoutesUtils.paths.w.palmtop.path]);
		// 	}
		// });
	}

	public reset() {
		this.loggerService.logInfo("[AuthStateService] Resetting auth state");

		this.currentUserId.set(undefined);
		this.currentTenantId.set(undefined);
		this.currentAuthSession.set(null);
		this.currentUserInfo.set(null);
		this.currentAppRole.set(null);
		this.currentTenantInfo.set(null);
		this.currentTenantRole.set(null);

		localStorage.removeItem("userId");
		sessionStorage.removeItem("tenantId");
	}
}
